import React from 'react';

import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import CardContainer from 'components/cards/CardContainer';
import { H5, S } from 'components/typography';
import { useChargeCodesOptions } from '../charges-invoices/hooks/useChargeCodesOptions';
import styles from './styles.scss';
import { ChargeCodeToggleSection } from './ChargeCodeToggleSection';

type BillingReportProps = {
  channelId: string | undefined;
  showMarkupToggle?: boolean;
  isReportMarkupEnabled: boolean;
  markupChargeCodeId: string | null;
  updateIsReportMarkupEnabled: (value: boolean) => void;
  updateMarkupChargeCodeId: (value: string | null) => void;
  showAdminFeeToggle?: boolean;
  isReportAdminFeeEnabled: boolean;
  adminFeeChargeCodeId: string | null;
  updateIsReportAdminFeeEnabled: (value: boolean) => void;
  updateAdminFeeChargeCodeId: (value: string | null) => void;
  showTaxToggle?: boolean;
  isReportTaxEnabled: boolean;
  taxChargeCodeId: string | null;
  updateIsReportTaxEnabled: (value: boolean) => void;
  updateTaxChargeCodeId: (value: string | null) => void;
};

export function BillingReport({
  channelId,
  showMarkupToggle = false,
  isReportMarkupEnabled,
  markupChargeCodeId,
  updateIsReportMarkupEnabled,
  updateMarkupChargeCodeId,
  showAdminFeeToggle = false,
  isReportAdminFeeEnabled,
  adminFeeChargeCodeId,
  updateIsReportAdminFeeEnabled,
  updateAdminFeeChargeCodeId,
  showTaxToggle = false,
  isReportTaxEnabled,
  taxChargeCodeId,
  updateIsReportTaxEnabled,
  updateTaxChargeCodeId,
}: BillingReportProps) {
  const chargeCodeOptions = useChargeCodesOptions(channelId);
  const { t } = useTranslation();

  const handleToggle = async () => {
    if (isReportMarkupEnabled) {
      try {
        await window.Alert.confirm(turnOffConfirmationDialog(t));
        updateIsReportMarkupEnabled(false);
      } catch {
        // User cancelled - do nothing
      }
    } else {
      updateIsReportMarkupEnabled(true);
    }
  };

  const handleAdminFeeToggle = async () => {
    if (isReportAdminFeeEnabled) {
      try {
        await window.Alert.confirm(turnOffAdminFeeConfirmationDialog(t));
        updateIsReportAdminFeeEnabled(false);
      } catch {
        // User cancelled - do nothing
      }
    } else {
      updateIsReportAdminFeeEnabled(true);
    }
  };

  const handleTaxToggle = async () => {
    if (isReportTaxEnabled) {
      try {
        await window.Alert.confirm(turnOffTaxConfirmationDialog(t));
        updateIsReportTaxEnabled(false);
      } catch {
        // User cancelled - do nothing
      }
    } else {
      updateIsReportTaxEnabled(true);
    }
  };

  const chargeCodePlaceholder = t(
    'abp.billingAndPaymentSettings.billingReport.chargeCodePlaceholder'
  );

  return (
    <CardContainer className={styles.PaymentSettings}>
      <div className={styles.SectionHeader}>
        <H5>{t('abp.billingAndPaymentSettings.billingReport.header')}</H5>
        <S mt={3}>
          {t('abp.billingAndPaymentSettings.billingReport.description')}
        </S>
      </div>

      {showMarkupToggle && (
        <ChargeCodeToggleSection
          isEnabled={isReportMarkupEnabled}
          onToggle={handleToggle}
          chargeCodeId={markupChargeCodeId}
          onChargeCodeChange={(option) => updateMarkupChargeCodeId(option.value)}
          chargeCodeOptions={chargeCodeOptions}
          toggleLabel={t('abp.billingAndPaymentSettings.billingReport.toggleLabel')}
          toggleDescription={t('abp.billingAndPaymentSettings.billingReport.toggleDescription')}
          chargeCodeLabel={t('abp.billingAndPaymentSettings.billingReport.chargeCodeLabel')}
          chargeCodePlaceholder={chargeCodePlaceholder}
          toggleTestId="billingReportMarkupToggle"
          dropdownTestId="markupChargeCodeDropdown"
        />
      )}

      {showAdminFeeToggle && (
        <ChargeCodeToggleSection
          isEnabled={isReportAdminFeeEnabled}
          onToggle={handleAdminFeeToggle}
          chargeCodeId={adminFeeChargeCodeId}
          onChargeCodeChange={(option) => updateAdminFeeChargeCodeId(option.value)}
          chargeCodeOptions={chargeCodeOptions}
          toggleLabel={t('abp.billingAndPaymentSettings.billingReport.adminFeeToggleLabel')}
          toggleDescription={t('abp.billingAndPaymentSettings.billingReport.adminFeeToggleDescription')}
          chargeCodeLabel={t('abp.billingAndPaymentSettings.billingReport.adminFeeChargeCodeLabel')}
          chargeCodePlaceholder={chargeCodePlaceholder}
          toggleTestId="billingReportAdminFeeToggle"
          dropdownTestId="adminFeeChargeCodeDropdown"
        />
      )}

      {showTaxToggle && (
        <ChargeCodeToggleSection
          isEnabled={isReportTaxEnabled}
          onToggle={handleTaxToggle}
          chargeCodeId={taxChargeCodeId}
          onChargeCodeChange={(option) => updateTaxChargeCodeId(option.value)}
          chargeCodeOptions={chargeCodeOptions}
          toggleLabel={t('abp.billingAndPaymentSettings.billingReport.taxToggleLabel')}
          toggleDescription={t('abp.billingAndPaymentSettings.billingReport.taxToggleDescription')}
          chargeCodeLabel={t('abp.billingAndPaymentSettings.billingReport.taxChargeCodeLabel')}
          chargeCodePlaceholder={chargeCodePlaceholder}
          toggleTestId="billingReportTaxToggle"
          dropdownTestId="taxChargeCodeDropdown"
        />
      )}
    </CardContainer>
  );
}

function turnOffConfirmationDialog(t: TFunction<'translation', undefined>) {
  return {
    title: t('abp.billingAndPaymentSettings.billingReport.turnOffConfirmTitle'),
    message: t(
      'abp.billingAndPaymentSettings.billingReport.turnOffConfirmMessage'
    ),
    confirmText: t(
      'abp.billingAndPaymentSettings.billingReport.turnOffConfirmButton'
    ),
    cancelText: t(
      'abp.billingAndPaymentSettings.billingReport.turnOffCancelButton'
    ),
  };
}

function turnOffAdminFeeConfirmationDialog(
  t: TFunction<'translation', undefined>
) {
  return {
    title: t(
      'abp.billingAndPaymentSettings.billingReport.adminFeeTurnOffConfirmTitle'
    ),
    message: t(
      'abp.billingAndPaymentSettings.billingReport.adminFeeTurnOffConfirmMessage'
    ),
    confirmText: t(
      'abp.billingAndPaymentSettings.billingReport.adminFeeTurnOffConfirmButton'
    ),
    cancelText: t(
      'abp.billingAndPaymentSettings.billingReport.adminFeeTurnOffCancelButton'
    ),
  };
}

function turnOffTaxConfirmationDialog(t: TFunction<'translation', undefined>) {
  return {
    title: t(
      'abp.billingAndPaymentSettings.billingReport.taxTurnOffConfirmTitle'
    ),
    message: t(
      'abp.billingAndPaymentSettings.billingReport.taxTurnOffConfirmMessage'
    ),
    confirmText: t(
      'abp.billingAndPaymentSettings.billingReport.taxTurnOffConfirmButton'
    ),
    cancelText: t(
      'abp.billingAndPaymentSettings.billingReport.taxTurnOffCancelButton'
    ),
  };
}
